import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Popconfirm,
  Spin,
  Table,
  Tooltip,
  message,
} from 'antd';
import ContentLayout from '../../layout/ContentLayout';
import {
  fetchRolesAndPermissions,
  resetRolePermissions,
  updateRolePermissions,
} from '../../api/role-permission-api';
import { usePermissions } from '../../context/PermissionsContext';

// Roles whose users only see their own network. Giving one of them scope:all opens the whole
// platform to every user with that role, so the page asks for a second look.
const NETWORK_ROLES = ['doctor', 'clinic_owner', 'promoter', 'agent'];

// Serbian labels for the six roles the API accepts. Anything the API adds later still
// renders - it just falls back to the raw role key.
const ROLE_LABELS = {
  super_admin: 'Super administrator',
  admin: 'Administrator',
  manager: 'Menadžer',
  doctor: 'Mentor',
  clinic_owner: 'Klinika',
  promoter: 'Promoter',
  agent: 'Agent',
  client: 'Korisnik',
};

// Permission keys are grouped by their prefix so the table reads as sections rather than
// as one flat list of 23 rows.
const GROUP_LABELS = {
  scope: 'Obim pristupa',
  users: 'Korisnici',
  data: 'Kategorije podataka',
  ai: 'AI (sažeci, pravila, konzola)',
  catalog: 'Katalog',
  commerce: 'Prodaja',
  network: 'Mreža saradnika',
  content: 'Sadržaj',
  loyalty: 'Lojalnost',
  reports: 'Izveštaji',
  roles: 'Role i permisije',
  audit: 'Istorija akcija',
  system: 'Sistem',
};

// Serbian copy for the permission catalogue. The API returns English descriptions - they
// are developer-facing metadata (also used in Swagger), while this dashboard is the
// client's own screen and stays Serbian. A key that is missing here falls back to the
// API's text, so a newly added permission still shows something rather than nothing.
const PERMISSION_LABELS = {
  'scope:all':
    'Pristup svim korisnicima, narudžbinama i saradnicima na platformi, ne samo sopstvenoj mreži',
  'users:read': 'Pregled liste korisnika i pojedinačnih profila',
  'users:staff:read': 'Uvid u naloge administratora i super administratora',
  'users:write': 'Izmena podataka o korisniku',
  'users:create': 'Kreiranje novih korisnika',
  'users:delete': 'Brisanje korisničkih naloga',
  'users:status': 'Blokiranje, odblokiranje i verifikacija korisnika',
  'users:role': 'Promena role korisnika',
  'data:contact:read': 'Uvid u e-mail adrese i brojeve telefona',
  'data:survey:read': 'Uvid u odgovore iz upitnika',
  'data:health:read': 'Uvid u zdravstvena stanja, terapiju i povezane odgovore',
  'data:orders:read': 'Uvid u istoriju kupovina',
  'data:activity:read': 'Uvid u podsetnike, praćenje i aktivnosti iz kalendara',
  'ai:summary:read': 'Čitanje AI generisanih sažetaka o korisniku',
  'ai:summary:generate': 'Pokretanje osvežavanja AI sažetka',
  'ai:debug:read':
    'Otvaranje debug konzole AI motora (izvorni podaci, ulaz modela, potrošnja tokena)',
  'ai:debug:run':
    'Pokretanje akcija u debug konzoli koje pozivaju AI model i stvaraju trošak',
  'ai:outreach:read': 'Pregled AI pravila i podešavanja poruka koje asistent sam šalje',
  'ai:outreach:manage':
    'Pravljenje, izmena i brisanje AI pravila (i predlozi AI-a) i izmena podešavanja slanja',
  'catalog:manage':
    'Upravljanje proizvodima, kategorijama, bolestima i protokolima',
  'commerce:orders:read': 'Pregled narudžbina',
  'commerce:orders:manage': 'Izmena statusa i podataka narudžbine',
  'commerce:subscriptions:manage': 'Upravljanje pretplatama na proizvode',
  'commerce:commissions:manage':
    'Označavanje isplata provizija i donacija, izmena referral lanaca i provizija po proizvodu',
  'network:manage': 'Upravljanje mentorima, promoterima, agentima i klinikama',
  'content:manage':
    'Upravljanje objavama, notifikacijama, kvizovima i sadržajem u aplikaciji',
  'loyalty:manage': 'Upravljanje lojalnošću, poenima i referral konfiguracijom',
  'reports:read': 'Pregled izveštaja i statistike',
  'roles:read': 'Pregled matrice rola i permisija',
  'roles:manage': 'Izmena permisija koje rola ima (samo super administrator)',
  'audit:read': 'Pregled istorije administratorskih akcija',
  'system:config': 'Izmena zahteva za verziju aplikacije i pokretanje održavanja',
};

const groupOf = (key) => key.split(':')[0];

const RoleAndPermissions = () => {
  const [catalogue, setCatalogue] = useState([]);
  const [roles, setRoles] = useState([]);
  // role -> Set of permission keys, edited locally until saved.
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: loadError } = await fetchRolesAndPermissions();
    if (loadError) {
      setError(loadError);
      setLoading(false);
      return;
    }
    setError(null);
    setCatalogue(data.catalogue);
    setRoles(data.roles);
    setDraft(
      data.roles.reduce((acc, r) => {
        acc[r.role] = new Set(r.permissions);
        return acc;
      }, {})
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saved = useMemo(
    () =>
      roles.reduce((acc, r) => {
        acc[r.role] = new Set(r.permissions);
        return acc;
      }, {}),
    [roles]
  );

  const isDirty = (role) => {
    const a = draft[role] ?? new Set();
    const b = saved[role] ?? new Set();
    return a.size !== b.size || [...a].some((p) => !b.has(p));
  };

  // Only the super admin holds roles:manage; everyone else sees the matrix read-only.
  const { can } = usePermissions();
  const canManage = can('roles:manage');

  const toggle = (role, permission, checked) => {
    if (checked && permission === 'scope:all' && NETWORK_ROLES.includes(role)) {
      message.warning(
        `Rola „${ROLE_LABELS[role] ?? role}" će videti sve korisnike i narudžbine na platformi, ne samo svoju mrežu.`
      );
    }
    setDraft((prev) => {
      const next = new Set(prev[role] ?? []);
      if (checked) next.add(permission);
      else next.delete(permission);
      return { ...prev, [role]: next };
    });
  };

  // Each role is one row on the server, so saving is a small batch of writes. Only changed
  // roles are sent, and every result is folded back so the table matches the server even if
  // one of them fails.
  const applyResults = (results) => {
    const failures = [];
    results.forEach((x) => {
      if (!x.data) failures.push(ROLE_LABELS[x.role] ?? x.role);
    });
    setRoles((prev) =>
      prev.map((r) => {
        const hit = results.find((x) => x.role === r.role && x.data);
        return hit ? { ...r, permissions: hit.data.permissions } : r;
      })
    );
    setDraft((prev) => {
      const next = { ...prev };
      results.forEach((x) => {
        if (x.data) next[x.role] = new Set(x.data.permissions);
      });
      return next;
    });
    return failures;
  };

  const saveAll = async () => {
    const changed = roles.filter((r) => r.editable && isDirty(r.role));
    if (changed.length === 0) return;

    setSaving(true);
    const results = await Promise.all(
      changed.map(async (r) => {
        const { data, error: saveError } = await updateRolePermissions(r.role, [
          ...(draft[r.role] ?? []),
        ]);
        return { role: r.role, data, error: saveError };
      })
    );
    setSaving(false);

    const failures = applyResults(results);
    if (failures.length > 0) {
      message.error(`Nije sačuvano za: ${failures.join(', ')}.`);
      return;
    }
    message.success(
      changed.length === 1
        ? `Permisije za rolu „${ROLE_LABELS[changed[0].role] ?? changed[0].role}" su sačuvane.`
        : 'Permisije su sačuvane.'
    );
  };

  const resetAll = async () => {
    setSaving(true);
    const results = await Promise.all(
      roles
        .filter((r) => r.editable)
        .map(async (r) => {
          const { data, error: resetError } = await resetRolePermissions(
            r.role
          );
          return { role: r.role, data, error: resetError };
        })
    );
    setSaving(false);

    const failures = applyResults(results);
    if (failures.length > 0) {
      message.error(
        `Nije vraćeno na podrazumevano za: ${failures.join(', ')}.`
      );
      return;
    }
    message.success('Sve role su vraćene na podrazumevane permisije.');
  };

  const hasChanges = roles.some((r) => r.editable && isDirty(r.role));

  const rows = useMemo(() => {
    const byGroup = {};
    catalogue.forEach((p) => {
      const g = groupOf(p.key);
      byGroup[g] = byGroup[g] ?? [];
      byGroup[g].push(p);
    });
    return Object.entries(byGroup).flatMap(([g, items]) => [
      { key: `group-${g}`, isGroup: true, label: GROUP_LABELS[g] ?? g },
      ...items.map((p) => ({
        key: p.key,
        label: p.key,
        description: PERMISSION_LABELS[p.key] ?? p.description,
        grantable: p.grantable !== false,
      })),
    ]);
  }, [catalogue]);

  const columns = useMemo(
    () => [
      {
        title: 'Permisija',
        dataIndex: 'label',
        key: 'label',
        width: 320,
        render: (label, row) =>
          row.isGroup ? (
            <strong>{label}</strong>
          ) : (
            <Tooltip title={row.description}>
              <span>{label}</span>
            </Tooltip>
          ),
      },
      ...roles.map((r) => ({
        title: ROLE_LABELS[r.role] ?? r.role,
        key: r.role,
        align: 'center',
        render: (_, row) =>
          row.isGroup ? null : (
            <Checkbox
              // Fixed roles (super admin: everything, client: nothing) and super-admin-only
              // permissions can't be changed; the API refuses them too.
              disabled={!canManage || !r.editable || !row.grantable}
              checked={
                r.editable
                  ? (draft[r.role]?.has(row.key) ?? false)
                  : r.permissions.includes(row.key)
              }
              onChange={(e) => toggle(r.role, row.key, e.target.checked)}
            />
          ),
      })),
    ],
    [roles, draft, canManage]
  );

  if (loading) {
    return (
      <ContentLayout header="Role i permisije">
        <Spin />
      </ContentLayout>
    );
  }

  if (error) {
    return (
      <ContentLayout header="Role i permisije">
        <p>{error}</p>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      header={
        <>
          <h1 className="text-2xl font-bold">Role i permisije</h1>
          <p style={{ margin: 0 }}>
            Permisije određuju šta korisnik sa datom rolom može da vidi i radi,
            uključujući stavke menija. Super administrator uvek ima sve permisije,
            a korisnici aplikacije nemaju nijednu. Izmene vidi korisnik posle
            osvežavanja stranice.
          </p>
        </>
      }
    >
      <Table
        columns={columns}
        dataSource={rows}
        pagination={false}
        size="small"
        rowClassName={(row) => (row.isGroup ? 'permission-group-row' : '')}
      />

      {canManage && (
      <div
        style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 20 }}
      >
        <Button
          className="btn-primary"
          type="primary"
          disabled={!hasChanges}
          loading={saving}
          onClick={saveAll}
        >
          Sačuvaj
        </Button>
        <Popconfirm
          title="Vratiti sve role na podrazumevane permisije?"
          description="Sve sačuvane izmene biće poništene."
          okText="Vrati"
          cancelText="Odustani"
          onConfirm={resetAll}
        >
          <Button className="btn-tertiary" disabled={saving}>
            Podrazumevano
          </Button>
        </Popconfirm>
      </div>
      )}
    </ContentLayout>
  );
};

export default RoleAndPermissions;
